if (!customElements.get('cart-related-products')) {
  class CartRelatedProducts extends HTMLElement {
    constructor() {
      super();
      this.container = this;
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.getCart = this.getCart.bind(this);
      this.fetchRelatedProducts = this.fetchRelatedProducts.bind(this);
      this.renderProducts = this.renderProducts.bind(this);
    }

    connectedCallback() {
      // Listen to both events to catch all cart changes
      document.addEventListener('theme:cart:change', this.handleCartUpdate);
      document.addEventListener('theme:cart:init', this.handleCartUpdate);

      // Initial load
      this.getCart();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.handleCartUpdate);
      document.removeEventListener('theme:cart:init', this.handleCartUpdate);
    }

    handleCartUpdate(event) {
      this.getCart();
    }

    async getCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        if (cart.items?.length) {
          const productId = cart.items[0].product_id;
          await this.fetchRelatedProducts(productId);
        } else {
          this.style.display = 'none';
        }
      } catch (error) {
        console.error('Error fetching cart:', error);
        this.style.display = 'none';
      }
    }

    async fetchRelatedProducts(productId) {
      try {
        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=4`);
        const data = await response.json();

        if (data.products && data.products.length > 0) {
          this.renderProducts(data.products);
        } else {
          await this.fetchSameCollectionProducts(productId);
        }
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        this.style.display = 'none';
      }
    }

    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = theme.moneyFormat || '${{amount}}';

      function defaultTo(value, defaultValue) {
        return value == null || value !== value ? defaultValue : value;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ',');
        decimal = defaultTo(decimal, '.');

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const cents = parts[1] ? (decimal + parts[1]) : '';

        return dollars + cents;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }

    renderProducts(products) {

      if (!products.length) {
        this.style.display = 'none';
        return;
      }

      try {
        const sliderHtml = `
          <div class="cart-drawer__related-title">You May Also Like</div>
          <div class="cart-drawer__related-slider" data-slider>
            <div class="cart-drawer__related-wrapper">
              <div class="cart-drawer__related-grid">
                ${products.map(product => {
                  return `
                    <div class="cart-drawer__related-item">
                      <a href="/products/${product.handle}" class="cart-drawer__related-link">
                        <img
                          src="${product.featured_image}"
                          alt="${product.title}"
                          loading="lazy"
                          width="150"
                          height="150"
                          class="cart-drawer__related-image"
                        >
                        <div class="cart-drawer__related-details">
                          <p class="cart-drawer__related-product-title">${product.title}</p>
                          <p class="cart-drawer__related-price">${this.formatMoney(product.variants[0].price)}</p>
                        </div>
                      </a>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
            <button type="button" class="cart-drawer__related-prev" aria-label="Previous" data-prev>
              <svg aria-hidden="true" focusable="false" role="presentation" width="8" height="14" viewBox="0 0 8 14">
                <path d="M7 13L1 7l6-6" stroke="currentColor" stroke-width="2" fill="none" fill-rule="evenodd"></path>
              </svg>
            </button>
            <button type="button" class="cart-drawer__related-next" aria-label="Next" data-next>
              <svg aria-hidden="true" focusable="false" role="presentation" width="8" height="14" viewBox="0 0 8 14">
                <path d="M1 13l6-6-6-6" stroke="currentColor" stroke-width="2" fill="none" fill-rule="evenodd"></path>
              </svg>
            </button>
          </div>
        `;

        this.style.display = 'block';
        this.innerHTML = sliderHtml;
        this.initSlider();
      } catch (error) {
        console.error('Error rendering products:', error);
      }
    }

    initSlider() {
      const slider = this.querySelector('[data-slider]');
      const wrapper = this.querySelector('.cart-drawer__related-wrapper');
      const grid = this.querySelector('.cart-drawer__related-grid');
      const prevButton = this.querySelector('[data-prev]');
      const nextButton = this.querySelector('[data-next]');

      let currentPosition = 0;
      const slideWidth = wrapper.offsetWidth;

      const updateSliderPosition = () => {
        grid.style.transform = `translateX(${currentPosition}px)`;
        updateArrowVisibility(); // Changed to call local function
      };

      // Moved function outside
      const updateArrowVisibility = () => {
        prevButton.style.display = currentPosition < 0 ? 'block' : 'none';
        nextButton.style.display = (Math.abs(currentPosition) < (grid.scrollWidth - wrapper.offsetWidth)) ? 'block' : 'none';
      };

      prevButton.addEventListener('click', () => {
        currentPosition = Math.min(currentPosition + slideWidth, 0);
        updateSliderPosition();
      });

      nextButton.addEventListener('click', () => {
        currentPosition = Math.max(currentPosition - slideWidth, -(grid.scrollWidth - wrapper.offsetWidth));
        updateSliderPosition();
      });

      // Initial arrow visibility
      updateArrowVisibility();

      // Update on resize
      window.addEventListener('resize', () => {
        currentPosition = 0;
        updateSliderPosition();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.handleCartUpdate);
      document.removeEventListener('cart:add', this.handleCartUpdate);
      document.removeEventListener('cart:error', this.handleCartUpdate);
    }
  }

  customElements.define('cart-related-products', CartRelatedProducts);
}